import axios, { AxiosRequestConfig } from 'axios';
import { ApiError } from '../models/errors/ApiError';
import { useAuthStore } from '@/stores/auth';
import { useSuperAdminCreditorAccessStore } from '@/stores/superAdminCreditorAccess';
import router from '@/router';

const { VUE_APP_API_BASE_URL: API_BASE_URL } = process.env;

/** Must match EnsureAuthenticatedUserHasCreditor / controllers when scope is missing. */
const CREDITOR_SCOPE_REQUIRED_MESSAGE = 'User is not associated with a creditor';

export const axiosClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
});

/** Must match EnsureAuthenticatedUserIsActive::ACCOUNT_INACTIVE_CODE */
const ACCOUNT_INACTIVE_CODE = 'account_inactive';

let refreshPromise: Promise<void> | null = null;
let forbiddenRedirectInProgress = false;
let accountInactiveRedirectInProgress = false;
let authExpiredInProgress = false;

const ensureSessionRefreshed = async () => {
    if (!refreshPromise) {
        const authStore = useAuthStore();
        refreshPromise = authStore.refreshUserSession().finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
};

/**
 * Called when the session is fully expired (refresh 401).
 * Tokens are already dead so we skip POST /api/logout and go directly
 * to the Laravel web /logout route which destroys the PHP session cookie
 * and redirects back to the frontend sign-in page.
 * In-memory guard prevents duplicate navigations within the same page lifetime.
 */
const handleAuthExpired = () => {
    if (authExpiredInProgress) return;
    authExpiredInProgress = true;

    const authStore = useAuthStore();
    authStore.clearSession(false);

    const signInUrl = `${window.location.origin}/#/sign-in`;
    window.location.href = `${API_BASE_URL}/logout?redirect=${encodeURIComponent(signInUrl)}`;
};

/** Inactive account: API clears token cookies; use full navigation to web logout (avoids XHR + 302 + CORS). */
const handleAccountInactive = () => {
    if (accountInactiveRedirectInProgress) return;
    accountInactiveRedirectInProgress = true;

    const authStore = useAuthStore();
    authStore.clearSession(false);
    authStore.logoutUser();
};

const handleMissingCreditorScope = () => {
    if (forbiddenRedirectInProgress) return;
    forbiddenRedirectInProgress = true;

    useSuperAdminCreditorAccessStore().clearAccess();

    const alreadyOnSuperAdmin = router.currentRoute.value.name === 'superadmin-creditors';
    if (alreadyOnSuperAdmin) {
        forbiddenRedirectInProgress = false;
        return;
    }

    router.push({ name: 'superadmin-creditors' }).finally(() => {
        forbiddenRedirectInProgress = false;
    });
};

const handleForbidden = () => {
    if (forbiddenRedirectInProgress) return;
    forbiddenRedirectInProgress = true;

    const alreadyOnDashboard = router.currentRoute.value.name === 'dashboard';

    if (alreadyOnDashboard) {
        window.location.reload();
        return;
    }

    router.push({ name: 'dashboard' }).finally(() => {
        window.location.reload();
    });
};

axiosClient.interceptors.response.use((response) => {
    return response;
}, async (error) => {
    const authStore = useAuthStore();
    const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean;
        skipAuthRetry?: boolean;
    };

    // If 401 on the refresh request itself, do not retry – log out fully to clear web session.
    if (error.response?.status === 401 && originalRequest.skipAuthRetry) {
        handleAuthExpired();
        return Promise.reject(error);
    }

    // If 401 and we haven't retried this request yet
    if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
            // Use a shared refresh promise so parallel 401s wait for one refresh and then all retry.
            await ensureSessionRefreshed();
            return axiosClient(originalRequest);
        } catch (refreshError) {
            // Refresh token expired too; log out fully to clear web session.
            handleAuthExpired();
            return Promise.reject(refreshError);
        }
    }

    const errorData = error.response?.data as { code?: string; message?: string } | undefined;
    if (error.response?.status === 403 && errorData?.code === ACCOUNT_INACTIVE_CODE) {
        handleAccountInactive();
        return Promise.reject(error);
    }

    // Superadmin cookie expired / missing while on a creditor-scoped screen.
    if (
        error.response?.status === 403
        && errorData?.message === CREDITOR_SCOPE_REQUIRED_MESSAGE
        && authStore.roleName === 'superadmin'
    ) {
        handleMissingCreditorScope();
        return Promise.reject(error);
    }

    // 403 Forbidden: user lacks permission for this resource — bounce to dashboard and reload.
    if (error.response?.status === 403) {
        handleForbidden();
        return Promise.reject(error);
    }

    return Promise.reject(error);
});

export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';

export interface RequestOptions {
    params?: Record<string, string | number | boolean | undefined>;
    timeoutMs?: number;
    headers?: Record<string, string>;
    body?: unknown;
    /** When true, 401 will not trigger a refresh retry (e.g. for the refresh request itself). */
    skipAuthRetry?: boolean;

}

export const apiRequest = async <T>(
    path: string,
    method: HTTPMethod,
    options: RequestOptions = {},
): Promise<T> => {
    const {
        params,
        timeoutMs = 150000,
        headers,
        body,
        skipAuthRetry,
    } = options;

    const config: AxiosRequestConfig = {
        url: path,
        method,
        params,
        timeout: timeoutMs,
        headers: {
            'Content-Type': 'application/json',
            ...headers,
        },
        data: body,
        ...(skipAuthRetry !== undefined && { skipAuthRetry }),
    };

    try {
        const response = await axiosClient.request<T>(config);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new ApiError(
                error.response.statusText || 'Request failed',
                error.response.status,
                error.response.data,
            );
        }

        throw error;
    }
};

